import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyProfile, updateMyProfile, publishMyProfile } from '../../lib/api';
import { getRateTypeLabel } from '../../lib/utils';
import { toast } from 'sonner';
import { WizardLayout, WizardQuestion, WizardSection } from '../../components/onboarding/WizardLayout';
import { ChipMultiSelect } from '../../components/onboarding/ChipMultiSelect';
import { TransitModeSelect, RadiusSlider } from '../../components/onboarding/TravelSelect';
import { RateInput } from '../../components/onboarding/RateInput';
import { ExperienceSelect } from '../../components/onboarding/ExperienceSelect';
import { ProfilePreviewCard } from '../../components/onboarding/ProfilePreviewCard';
import { CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import debounce from 'lodash.debounce';

const TOTAL_STEPS = 7; // 6 steps + publish screen

const CORE_SPECIALTIES = [
  'FOH Engineer', 'Monitor Engineer', 'Lighting Op', 'Backline Tech',
  'AV Tech', 'Stage Manager', 'Camera Op', 'Runner',
  'Live Recording', 'Videographer',
];

const MORE_SPECIALTIES = [
  'System Tech', 'Patch / Stage Tech', 'LX Programmer', 'Master Electrician',
  'VJ / Media Server Op', 'LED Tech', 'Rigging', 'Production Manager',
  'Pyrotechnics', 'SFX',
];

const DEFAULT_SPECIALTIES = [...CORE_SPECIALTIES, ...MORE_SPECIALTIES];

const EXPERT_HARDWARE = [
  // Audio Consoles
  'DiGiCo SD/Quantum', 'Avid S6L', 'Yamaha Rivage/PM', 'Midas M32', 'Allen & Heath dLive',
  // Lighting Desks
  'GrandMA3', 'GrandMA2', 'Hog 4', 'Chamsys MagicQ', 'Avolites Titan',
  // Video Gear
  'Barco E2', 'Blackmagic ATEM', 'Resolume', 'Disguise (d3)',
];

const TECHNICAL_MASTERY = [
  // Networking & Protocols
  'Dante', 'AVB', 'Milan', 'AES50',
  // Wireless/RF
  'Wireless Workbench', 'Sennheiser WSM', 'RF Coordination', 'IEM Management',
  // System Tuning
  'SMAART', 'L-Acoustics Network Manager', 'd&b ArrayCalc', 'PA Tuning',
];

const DEFAULT_EVENT_TYPES = [
  'Clubs', 'Festivals', 'Corporate', 'Weddings', 
  'Private events', 'Theatre', 'Touring', 'Studio sessions',
  'Bar/Cafe', 'Live Broadcasts'
];

const DEFAULT_LANGUAGES = [
  'Dutch', 'English', 'German', 'French', 'Spanish',
  'Portuguese', 'Italian', 'Turkish', 'Arabic', 'Polish'
];

export default function TechOnboardingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showMoreRoles, setShowMoreRoles] = useState(false);
  const [showHardware, setShowHardware] = useState(false);
  const [showMastery, setShowMastery] = useState(false);
  const [profile, setProfile] = useState({
    specialties: [],
    expertHardware: [],
    technicalMastery: [],
    eventTypes: [],
    languages: [],
    primaryTransitMode: [],
    travelRadiusKm: 50,
    rateAmount: null,
    onboardingRateType: 'hourly',
    techExperienceLevel: null,
  });

  // Load profile on mount
  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const response = await getMyProfile();
      const p = response.data;
      
      // Check if already published
      if (p.profileStatus === 'published') {
        navigate('/tech/me');
        return;
      }
      
      setProfile({
        ...profile,
        ...p,
        specialties: p.specialties || [],
        expertHardware: p.expertHardware || [],
        technicalMastery: p.technicalMastery || [],
        eventTypes: p.eventTypes || [],
        languages: p.languages || [],
        primaryTransitMode: Array.isArray(p.primaryTransitMode) ? p.primaryTransitMode : p.primaryTransitMode ? [p.primaryTransitMode] : [],
        travelRadiusKm: p.travelRadiusKm || 50,
        onboardingRateType: p.onboardingRateType || 'hourly',
      });
      
      // Resume at last step
      if (p.lastOnboardingStep && p.lastOnboardingStep <= TOTAL_STEPS) {
        setCurrentStep(p.lastOnboardingStep);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        navigate('/login');
        return;
      }
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Debounced autosave
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce(async (data) => {
      try {
        await updateMyProfile(data);
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error('Session expired. Please sign in again.');
          navigate('/login');
          return;
        }
        console.error('Autosave failed:', error);
      }
    }, 600),
    []
  );

  const updateField = (field, value) => {
    const updated = { ...profile, [field]: value };
    setProfile(updated);
    debouncedSave({ [field]: value });
  };

  const saveAndContinue = async () => {
    setSaving(true);
    try {
      const nextStep = currentStep + 1;
      await updateMyProfile({
        ...getStepData(),
        lastOnboardingStep: nextStep,
      });
      setCurrentStep(nextStep);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        navigate('/login');
        return;
      }
      // Show a human-readable message; never surface raw API error text
      // (e.g. enum validation dumps) to the user.
      toast.error('Could not save your changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getStepData = () => {
    switch (currentStep) {
      case 1:
        return { 
          specialties: profile.specialties,
          expertHardware: profile.expertHardware,
          technicalMastery: profile.technicalMastery,
        };
      case 2:
        return { eventTypes: profile.eventTypes };
      case 3:
        return { languages: profile.languages };
      case 4:
        return { 
          primaryTransitMode: profile.primaryTransitMode,
          travelRadiusKm: profile.travelRadiusKm,
        };
      case 5:
        return {
          rateAmount: profile.rateAmount,
          onboardingRateType: profile.onboardingRateType,
        };
      case 6:
        return { techExperienceLevel: profile.techExperienceLevel };
      default:
        return {};
    }
  };

  const canContinue = () => {
    switch (currentStep) {
      case 1:
        return profile.specialties.length > 0;
      case 2:
        return profile.eventTypes.length > 0;
      case 3:
        return profile.languages.length > 0;
      case 4:
        const transit = profile.primaryTransitMode;
        return Array.isArray(transit) ? transit.length > 0 : !!transit;
      case 5:
        return profile.rateAmount && profile.onboardingRateType;
      case 6:
        return profile.techExperienceLevel;
      case 7:
        return true;
      default:
        return false;
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await publishMyProfile();
      toast.success('Profile published! You are now visible in the directory.');
      navigate('/tech/me');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please sign in again.');
        navigate('/login');
        return;
      }
      const errors = error.response?.data?.detail?.errors;
      if (errors) {
        // Publish completeness errors are already human-readable.
        toast.error(errors.join(', '));
      } else {
        toast.error('Could not publish your profile. Please try again.');
      }
    } finally {
      setPublishing(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--s8-amber)]"></div>
      </div>
    );
  }

  // Publish screen (step 7)
  if (currentStep === 7) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] p-6 pb-24">
        <div className="w-full max-w-lg mx-auto">
          {/* Back button */}
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-white/40 hover:text-white/60 text-sm mb-6 transition-colors"
            data-testid="publish-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto bg-[color-mix(in_srgb,var(--s8-amber)_20%,transparent)] rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-[var(--s8-amber)]" />
            </div>
            <h1 className="text-2xl font-semibold text-white mb-2">Ready to Publish?</h1>
            <p className="text-white/50 text-sm">
              Review your profile below. Once published, you'll appear in the tech crew directory.
            </p>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-br from-[#1e1915] to-[#171411] border border-white/10 rounded-xl p-5 text-left mb-6 space-y-4">
            {/* Specialties */}
            <div>
              <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Roles</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {profile.specialties.map(s => (
                  <span key={s} className="px-2.5 py-1 text-xs rounded-md bg-[color-mix(in_srgb,var(--s8-amber)_15%,transparent)] text-[var(--s8-amber)] border border-[color-mix(in_srgb,var(--s8-amber)_30%,transparent)]">{s}</span>
                ))}
              </div>
            </div>

            {/* Expert Hardware */}
            {profile.expertHardware?.length > 0 && (
              <div>
                <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Hardware</span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {profile.expertHardware.map(h => (
                    <span key={h} className="px-2.5 py-1 text-xs rounded-md bg-white/[0.06] text-white/70 border border-white/10">{h}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Technical Mastery */}
            {profile.technicalMastery?.length > 0 && (
              <div>
                <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Mastery</span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {profile.technicalMastery.map(t => (
                    <span key={t} className="px-2.5 py-1 text-xs rounded-md bg-white/[0.06] text-white/70 border border-white/10">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-white/[0.06]" />

            {/* Event Types */}
            <div className="flex justify-between items-start">
              <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Events</span>
              <span className="text-white text-sm text-right">{profile.eventTypes.join(', ')}</span>
            </div>

            {/* Languages */}
            {profile.languages?.length > 0 && (
              <div className="flex justify-between items-start">
                <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Languages</span>
                <span className="text-white text-sm text-right">{profile.languages.join(', ')}</span>
              </div>
            )}

            {/* Travel */}
            <div className="flex justify-between">
              <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Travel</span>
              <span className="text-white text-sm">{profile.travelRadiusKm} km radius</span>
            </div>

            {/* Rate */}
            <div className="flex justify-between">
              <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Rate</span>
              <span className="text-[var(--s8-amber)] text-sm font-semibold">€{profile.rateAmount} <span className="text-white/50 font-normal">{getRateTypeLabel(profile.onboardingRateType)}</span></span>
            </div>

            {/* Experience */}
            {profile.techExperienceLevel && (
              <div className="flex justify-between">
                <span className="text-xs text-white/40 uppercase tracking-wider font-medium">Experience</span>
                <span className="text-white text-sm capitalize">{profile.techExperienceLevel.toLowerCase().replace('_', ' ')}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={handlePublish}
              disabled={publishing}
              className="w-full h-14 bg-[var(--s8-amber)] hover:bg-[var(--s8-amber)] text-black font-bold uppercase tracking-wide"
              data-testid="publish-profile-btn"
            >
              {publishing ? 'Publishing...' : 'Publish Profile'}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => navigate('/tech/me')}
              className="w-full h-12 border-white/10 text-white/70 hover:text-white hover:bg-white/5"
            >
              Add optional details first
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            
            <button
              onClick={() => navigate('/tech')}
              className="w-full text-white/40 text-sm hover:text-white/60 transition-colors"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <WizardLayout
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS - 1}
      onBack={handleBack}
      onContinue={saveAndContinue}
      canContinue={canContinue()}
      isLoading={saving}
      previewPanel={<ProfilePreviewCard profile={profile} profileType="tech" />}
    >
      {/* Step 1: Primary Roles + Hardware + Mastery */}
      {currentStep === 1 && (
        <>
          <WizardQuestion
            title="What's your role?"
            subtitle="Select your primary technical roles"
          />
          <ChipMultiSelect
            options={showMoreRoles ? DEFAULT_SPECIALTIES : CORE_SPECIALTIES}
            selected={profile.specialties}
            onChange={(val) => updateField('specialties', val)}
            placeholder="Search or add role..."
            maxItems={10}
            showSearch={showMoreRoles}
          />
          {!showMoreRoles && (
            <button
              type="button"
              onClick={() => setShowMoreRoles(true)}
              className="mt-2 text-xs text-[var(--s8-amber)] hover:text-white transition-colors"
            >
              + More roles
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowHardware(!showHardware)}
            className="mt-8 pt-6 border-t border-white/[0.06] w-full text-left flex items-center justify-between"
          >
            <div>
              <h3 className="text-sm text-white/60 uppercase tracking-wider font-semibold">Expert Hardware <span className="text-white/25 normal-case font-normal">· optional</span></h3>
              <p className="text-xs text-white/30 mt-0.5">Consoles, desks, and gear you operate</p>
            </div>
            <span className="text-xs text-[var(--s8-amber)]">{showHardware ? '−' : '+'}</span>
          </button>
          {showHardware && (
            <div className="mt-3">
              <ChipMultiSelect
                options={EXPERT_HARDWARE}
                selected={profile.expertHardware}
                onChange={(val) => updateField('expertHardware', val)}
                placeholder="Search or add hardware..."
                maxItems={10}
              />
            </div>
          )}

          <button
            type="button"
            onClick={() => setShowMastery(!showMastery)}
            className="mt-6 pt-6 border-t border-white/[0.06] w-full text-left flex items-center justify-between"
          >
            <div>
              <h3 className="text-sm text-white/60 uppercase tracking-wider font-semibold">Technical Mastery <span className="text-white/25 normal-case font-normal">· optional</span></h3>
              <p className="text-xs text-white/30 mt-0.5">Protocols, tuning, and RF skills</p>
            </div>
            <span className="text-xs text-[var(--s8-amber)]">{showMastery ? '−' : '+'}</span>
          </button>
          {showMastery && (
            <div className="mt-3">
              <ChipMultiSelect
                options={TECHNICAL_MASTERY}
                selected={profile.technicalMastery}
                onChange={(val) => updateField('technicalMastery', val)}
                placeholder="Search or add skill..."
                maxItems={10}
              />
            </div>
          )}
        </>
      )}

      {/* Step 2: Event Types */}
      {currentStep === 2 && (
        <>
          <WizardQuestion
            title="What kinds of events do you support?"
            subtitle="Select the types of events you work"
          />
          <ChipMultiSelect
            options={DEFAULT_EVENT_TYPES}
            selected={profile.eventTypes}
            onChange={(val) => updateField('eventTypes', val)}
            placeholder="Search or add event type..."
            maxItems={8}
          />
        </>
      )}

      {/* Step 3: Languages */}
      {currentStep === 3 && (
        <>
          <WizardQuestion
            title="What languages do you speak?"
            subtitle="Helps venues match you to the right crew"
          />
          <ChipMultiSelect
            options={DEFAULT_LANGUAGES}
            selected={profile.languages}
            onChange={(val) => updateField('languages', val)}
            placeholder="Search or add language..."
            maxItems={5}
          />
        </>
      )}

      {/* Step 4: Travel */}
      {currentStep === 4 && (
        <>
          <WizardQuestion
            title="How do you travel?"
            subtitle="Select your primary transport and travel radius"
          />
          <div className="space-y-8">
            <TransitModeSelect
              value={profile.primaryTransitMode}
              onChange={(val) => updateField('primaryTransitMode', val)}
            />
            <div className="pt-4 border-t border-white/[0.06]">
              <p className="text-xs text-white/40 uppercase tracking-wider text-center mb-4">
                Travel Radius
              </p>
              <RadiusSlider
                value={profile.travelRadiusKm}
                onChange={(val) => updateField('travelRadiusKm', val)}
              />
            </div>
          </div>
        </>
      )}

      {/* Step 5: Rate */}
      {currentStep === 5 && (
        <>
          <WizardQuestion
            title="What is your rate structure?"
            subtitle="Set your base rate and pricing model"
          />
          <RateInput
            amount={profile.rateAmount}
            rateType={profile.onboardingRateType}
            onAmountChange={(val) => updateField('rateAmount', val)}
            onRateTypeChange={(val) => updateField('onboardingRateType', val)}
            profileType="tech"
          />
        </>
      )}

      {/* Step 6: Experience */}
      {currentStep === 6 && (
        <>
          <WizardQuestion
            title="How experienced are you?"
            subtitle="This helps venues find the right fit"
          />
          <ExperienceSelect
            value={profile.techExperienceLevel}
            onChange={(val) => updateField('techExperienceLevel', val)}
            profileType="tech"
          />
        </>
      )}
    </WizardLayout>
  );
}
